/**
 * OrganizationContent - Organization settings panel
 * 
 * Admin-only panel for managing organization settings,
 * team overview, and company information.
 */

import { useState, useEffect } from 'react';
import { Button, Label, Input } from '@/components/ui';
import { logger } from '../../utils/logger';

interface OrganizationContentProps {
  // Future: organization data from backend
}

interface TeamMember {
  id: string;
  email: string;
  name: string;
  role: string;
  groups: string[];
  lastActive?: string;
}

export function OrganizationContent(_props: OrganizationContentProps) {
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [orgName, setOrgName] = useState('Aerotage');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Load organization data
  useEffect(() => {
    const loadOrgData = async () => {
      try {
        // TODO: Fetch from backend
        // Simulated data for now
        setTeamMembers([
          { id: '1', email: 'admin@aerotage.com', name: 'Admin User', role: 'Administrator', groups: ['Administrators'], lastActive: '2 hours ago' },
          { id: '2', email: 'engineer@aerotage.com', name: 'Engineer User', role: 'Engineer', groups: ['Engineers'], lastActive: '1 day ago' },
        ]);
      } catch (error) {
        logger.error('Failed to load organization data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadOrgData();
  }, []);
  
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    
    try {
      // TODO: Save to backend
      setMessage({ type: 'success', text: 'Organization settings saved' });
    } catch (_error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading organization data...</div>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header - matches DocumentViewerContent styling */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 py-2 bg-background">
        <h2 className="text-sm font-medium text-foreground flex-1">
          Organization
        </h2>
      </div>
      
      {/* Content - matches DocumentViewerContent styling */}
      <div className="flex-1 overflow-y-auto p-4 bg-card space-y-4">
        {/* Message */}
        {message && (
          <div className={`p-3 rounded-lg ${
            message.type === 'success' 
              ? 'bg-success-light border border-success text-success'
              : 'bg-destructive-light border border-destructive text-destructive'
          }`}>
            <p className="text-sm">{message.text}</p>
          </div>
        )}
        
        {/* Organization Info */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Organization Details
          </h3>
          
          <div className="space-y-4">
            <div>
              <Label className="block text-sm font-medium text-foreground mb-1">
                Organization Name
              </Label>
              <Input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full"
              />
            </div>
            
            <Button
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
        
        {/* Team Overview */}
        <div className="bg-muted/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground">
              Team Members
            </h3>
            <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
              {teamMembers.length} members
            </span>
          </div>
          
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {teamMembers.map((member) => (
              <div key={member.id} className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {member.email.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {member.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {member.email}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-foreground">
                    {member.role}
                  </p>
                  {member.lastActive && (
                    <p className="text-xs text-muted-foreground">
                      Active {member.lastActive}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4">
            <p className="text-xs text-muted-foreground">
              Use the Users page to manage team members and permissions
            </p>
          </div>
        </div>
        
        {/* Cognito Groups */}
        <div className="bg-muted/30 rounded-lg p-6">
          <h3 className="text-sm font-semibold text-foreground mb-4">
            Available Roles
          </h3>
          
          <div className="space-y-3">
            <RoleRow
              name="Administrators"
              description="Full access to all features including user management"
              color="red"
            />
            <RoleRow
              name="Engineers"
              description="Access to projects, documents, and engineering tools"
              color="blue"
            />
            <RoleRow
              name="Quality"
              description="Access to quality management and validation tools"
              color="green"
            />
            <RoleRow
              name="Procurement"
              description="Access to supplier management"
              color="amber"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Role row component
interface RoleRowProps {
  name: string;
  description: string;
  color: 'red' | 'blue' | 'green' | 'amber';
}

function RoleRow({ name, description, color }: RoleRowProps) {
  const colorClasses = {
    red: 'bg-destructive-light text-destructive',
    blue: 'bg-info-light text-info',
    green: 'bg-success-light text-success',
    amber: 'bg-warning-light text-warning',
  };
  
  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
      <span className={`px-2 py-1 rounded text-xs font-medium ${colorClasses[color]}`}>
        {name}
      </span>
      <span className="text-sm text-muted-foreground">
        {description}
      </span>
    </div>
  );
}
